import {
  ComputeNode,
  TopologicalSortResult,
} from '@shared/models/pipeline/compute.model';
import { profiler } from './pipeline-profiler';

/**
 * Perform topological sort on compute nodes using Kahn's algorithm.
 * Returns nodes in order where dependencies come before dependents.
 * Also detects and reports any circular dependencies.
 *
 * @param nodes - Array of compute nodes to sort
 * @returns Sorted nodes and any detected cycles
 */
export const topologicalSort = <T>(
  nodes: ComputeNode<T>[],
): TopologicalSortResult<ComputeNode<T>> => {
  profiler.time('topologicalSort');
  profiler.count('topologicalSort:calls');
  profiler.countAdd('topologicalSort:nodes', nodes.length);
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const inDegree = new Map<string, number>();
  const adjacencyList = new Map<string, string[]>();

  // Initialize in-degree and adjacency list
  for (const node of nodes) {
    if (!inDegree.has(node.id)) {
      inDegree.set(node.id, 0);
    }
    if (!adjacencyList.has(node.id)) {
      adjacencyList.set(node.id, []);
    }

    for (const dep of node.dependencies) {
      // Only count dependencies that exist in our node set
      if (nodeMap.has(dep)) {
        inDegree.set(node.id, (inDegree.get(node.id) ?? 0) + 1);

        const depList = adjacencyList.get(dep) ?? [];
        depList.push(node.id);
        adjacencyList.set(dep, depList);
      }
    }
  }

  // Find all nodes with no dependencies (in-degree 0)
  const queue: string[] = [];
  for (const [id, degree] of inDegree) {
    if (degree === 0) {
      queue.push(id);
    }
  }

  const sorted: ComputeNode<T>[] = [];

  // Process nodes in order (pointer avoids O(n) shift)
  let front = 0;
  while (front < queue.length) {
    const id = queue[front++]!;
    const node = nodeMap.get(id);

    if (node) {
      sorted.push(node);
    }

    // Reduce in-degree of dependents
    const dependents = adjacencyList.get(id) ?? [];
    for (const depId of dependents) {
      const newDegree = (inDegree.get(depId) ?? 1) - 1;
      inDegree.set(depId, newDegree);

      if (newDegree === 0) {
        queue.push(depId);
      }
    }
  }

  // Detect cycles - any remaining nodes with in-degree > 0 are in cycles
  const cycles = detectCycles(nodes, sorted);
  profiler.timeEnd('topologicalSort');

  return { sorted, cycles };
};

/**
 * Detect circular dependencies in the graph.
 * Uses DFS to find all cycles.
 *
 * @param nodes - All nodes in the graph
 * @param sorted - Successfully sorted nodes (not in cycles)
 * @returns Array of cycles, where each cycle is an array of node IDs
 */
const detectCycles = <T>(
  nodes: ComputeNode<T>[],
  sorted: ComputeNode<T>[],
): string[][] => {
  const sortedIds = new Set(sorted.map((n) => n.id));
  const unsorted = nodes.filter((n) => !sortedIds.has(n.id));

  if (unsorted.length === 0) {
    return [];
  }

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const cycles: string[][] = [];
  const visited = new Set<string>();
  const recStack = new Set<string>();

  const dfs = (nodeId: string, path: string[]): void => {
    if (recStack.has(nodeId)) {
      // Found a cycle - extract it from path
      const cycleStart = path.indexOf(nodeId);
      if (cycleStart !== -1) {
        cycles.push(path.slice(cycleStart));
      }
      return;
    }

    if (visited.has(nodeId)) {
      return;
    }

    visited.add(nodeId);
    recStack.add(nodeId);

    const node = nodeMap.get(nodeId);
    if (node) {
      for (const dep of node.dependencies) {
        if (nodeMap.has(dep)) {
          dfs(dep, [...path, nodeId]);
        }
      }
    }

    recStack.delete(nodeId);
  };

  // Start DFS from unsorted nodes
  for (const node of unsorted) {
    if (!visited.has(node.id)) {
      dfs(node.id, []);
    }
  }

  return cycles;
};

/**
 * Detect circular dependencies in a set of nodes.
 * Returns an array of cycles found.
 *
 * @param nodes - Array of compute nodes to check
 * @returns Array of cycles, where each cycle is an array of node IDs
 */
export const detectCircularDependencies = <T>(
  nodes: ComputeNode<T>[],
): string[][] => {
  const { cycles } = topologicalSort(nodes);
  return cycles;
};

/**
 * Check if a node would create a circular dependency if added to the graph.
 *
 * @param existingNodes - Current nodes in the graph
 * @param newNode - Node to check
 * @returns true if adding the node would create a cycle
 */
export const wouldCreateCycle = <T>(
  existingNodes: ComputeNode<T>[],
  newNode: ComputeNode<T>,
): boolean => {
  const allNodes = [...existingNodes, newNode];
  const cycles = detectCircularDependencies(allNodes);
  return cycles.length > 0;
};

/**
 * Resolve all nodes in dependency order.
 * Returns a record of all resolved values.
 *
 * @param nodes - Nodes to resolve
 * @returns Record of resolved values keyed by node ID
 * @throws Error if circular dependencies exist
 */
export const resolveNodes = <T>(nodes: ComputeNode<T>[]): Record<string, T> => {
  const { sorted, cycles } = topologicalSort(nodes);

  if (cycles.length > 0) {
    throw new Error(
      `Circular dependencies detected: ${cycles.map((c) => c.join(' -> ')).join(', ')}`,
    );
  }

  return resolveInOrder(sorted);
};

/**
 * Resolve nodes with relaxed cycle handling.
 * Acyclic nodes are resolved first in topological order.
 * Cycled nodes (e.g. mass ↔ density) are then resolved in multi-pass:
 * each compute closure falls back to its parentVariables for missing deps,
 * so product-derived seed values (like density from product) break the cycle.
 *
 * @param nodes - Nodes to resolve
 * @param maxPasses - Maximum passes for cycled nodes (default 3)
 * @returns Record of all resolved values
 */
export const resolveNodesRelaxed = <T>(
  nodes: ComputeNode<T>[],
  maxPasses = 3,
): Record<string, T> => {
  profiler.time('resolveNodesRelaxed');
  profiler.count('resolveNodesRelaxed:calls');
  const { sorted } = topologicalSort(nodes);
  const sortedIds = new Set(sorted.map((n) => n.id));
  const cycledNodes = nodes.filter((n) => !sortedIds.has(n.id));
  profiler.countAdd('resolveNodesRelaxed:cycledNodes', cycledNodes.length);

  // Phase 1: resolve acyclic nodes in topological order
  profiler.time('resolveNodesRelaxed:resolveInOrder');
  const resolved = resolveInOrder(sorted);
  profiler.timeEnd('resolveNodesRelaxed:resolveInOrder');

  // Phase 2: resolve cycled nodes with multi-pass relaxation
  let passCount = 0;
  for (let pass = 0; pass < maxPasses; pass++) {
    passCount++;
    let changed = false;
    for (const node of cycledNodes) {
      const deps = gatherDeps(node, resolved);
      const value = node.compute(deps);
      if (resolved[node.id] !== value) {
        resolved[node.id] = value;
        changed = true;
      }
    }
    if (!changed) break;
  }
  profiler.countAdd('resolveNodesRelaxed:passes', passCount);
  profiler.timeEnd('resolveNodesRelaxed');

  return resolved;
};

/** Resolve an already-sorted list of nodes sequentially */
const resolveInOrder = <T>(sorted: ComputeNode<T>[]): Record<string, T> => {
  const resolved: Record<string, T> = {};
  for (const node of sorted) {
    resolved[node.id] = node.compute(gatherDeps(node, resolved));
  }
  return resolved;
};

/** Gather resolved dependency values for a node */
const gatherDeps = <T>(
  node: ComputeNode<T>,
  resolved: Record<string, T>,
): Record<string, unknown> => {
  const deps: Record<string, unknown> = {};
  for (const depId of node.dependencies) {
    if (depId in resolved) {
      deps[depId] = resolved[depId];
    }
  }
  return deps;
};

/**
 * Get all dependencies for a node, including transitive dependencies.
 *
 * @param nodes - All nodes in the graph
 * @param nodeId - ID of the node to get dependencies for
 * @returns Set of all dependency IDs (direct and transitive)
 */
export const getAllDependencies = <T>(
  nodes: ComputeNode<T>[],
  nodeId: string,
): Set<string> => {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const dependencies = new Set<string>();
  const visited = new Set<string>();

  const traverse = (id: string): void => {
    if (visited.has(id)) return;
    visited.add(id);

    const node = nodeMap.get(id);
    if (!node) return;

    for (const depId of node.dependencies) {
      dependencies.add(depId);
      traverse(depId);
    }
  };

  traverse(nodeId);
  return dependencies;
};

/**
 * Get all nodes that depend on a given node (direct and transitive).
 *
 * @param nodes - All nodes in the graph
 * @param nodeId - ID of the node to get dependents for
 * @returns Set of all dependent node IDs
 */
export const getAllDependents = <T>(
  nodes: ComputeNode<T>[],
  nodeId: string,
): Set<string> => {
  const dependents = new Set<string>();
  const visited = new Set<string>();

  // Build reverse adjacency list
  const reverseAdj = new Map<string, string[]>();
  for (const node of nodes) {
    for (const depId of node.dependencies) {
      const list = reverseAdj.get(depId) ?? [];
      list.push(node.id);
      reverseAdj.set(depId, list);
    }
  }

  const traverse = (id: string): void => {
    if (visited.has(id)) return;
    visited.add(id);

    const deps = reverseAdj.get(id) ?? [];
    for (const depId of deps) {
      dependents.add(depId);
      traverse(depId);
    }
  };

  traverse(nodeId);
  return dependents;
};
